import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import SearchDropdown from '../../../components/ui/SearchDropdown';
import { useAuth } from '../../../contexts/AuthContext';
import { useSearch } from '../../../hooks/useSearch';

const HeroSection = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['homepage', 'common']);
  const { isAuthenticated } = useAuth();
  const [currentOccasion, setCurrentOccasion] = useState(0);

  // Use the search hook
  const {
    searchQuery,
    searchResults,
    searchSuggestions,
    searchHistory,
    isSearching,
    searchError,
    showDropdown,
    handleSearchChange,
    handleInputFocus,
    handleDropdownClose,
    handleKeyPress,
    saveSearchToHistory,
    clearSearchHistory,
    setShowDropdown
  } = useSearch();

  // Refs for search functionality
  const searchInputRef = useRef(null);
  const searchDropdownRef = useRef(null);

  const upcomingOccasions = t('homepage:hero.upcomingOccasions.occasions', { returnObjects: true });
  const staticSearchSuggestions = t('homepage:hero.searchSuggestions', { returnObjects: true });

  // Occasion rotation effect
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentOccasion((prev) => (prev + 1) % upcomingOccasions.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [upcomingOccasions.length]);

  // Click outside handler for search dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchDropdownRef.current &&
        !searchDropdownRef.current.contains(event.target) &&
        !searchInputRef.current.contains(event.target)
      ) {
        handleDropdownClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleDropdownClose]);

  // Handle search execution
  const handleSearch = (query = searchQuery) => {
    if (query.trim()) {
      saveSearchToHistory(query.trim());
      setShowDropdown(false);
      navigate('/gift-finder', { state: { query: query.trim() } });
    }
  };

  // Handle search suggestion click
  const handleSuggestionClick = (suggestion) => {
    handleSearch(suggestion);
  };

  // Handle product result click
  const handleProductClick = (product) => {
    setShowDropdown(false);
    navigate(`/products/${product.slug}`, { state: { product } });
  };

  // Handle history item click
  const handleHistoryClick = (historyItem) => {
    handleSearch(historyItem);
  };

  return (
    <section className="relative bg-gradient-to-br from-primary-50 via-white to-secondary-50 pt-24 pb-16 lg:pt-32 lg:pb-24 overflow-hidden">
      {/* Background Decorations */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-100 rounded-full opacity-20 animate-pulse"></div>
        <div className="absolute -bottom-20 -left-20 w-60 h-60 bg-secondary-100 rounded-full opacity-30 animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/4 w-4 h-4 bg-primary-300 rounded-full animate-bounce delay-500"></div>
        <div className="absolute top-1/3 right-1/3 w-3 h-3 bg-secondary-400 rounded-full animate-bounce delay-700"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-4xl mx-auto">
          {/* Upcoming Occasion Badge */}
          <div className="inline-flex items-center space-x-2 bg-white/80 backdrop-blur-sm rounded-full px-4 py-2 mb-6 shadow-soft border border-accent-100">
            <Icon name="Calendar" size={16} className="text-primary" />
            <span className="text-sm font-medium text-accent-700">
              {t('homepage:hero.upcomingOccasions.next')} {upcomingOccasions[currentOccasion]?.name} • {upcomingOccasions[currentOccasion]?.date}
            </span>
            <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
          </div>

          {/* Main Headline */}
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-accent-800 mb-6 leading-tight">
            {t('homepage:hero.title')}
            <span className="block text-gradient-primary">{t('homepage:hero.titleHighlight')}</span>
          </h1>

          {/* Subheadline */}
          <p className="text-lg sm:text-xl text-accent-600 mb-8 max-w-2xl mx-auto leading-relaxed">
            {t('homepage:hero.subtitle')}
          </p>

          {/* Enhanced Smart Search Bar */}
          <div className="max-w-2xl mx-auto mb-8 relative">
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-primary to-secondary rounded-2xl blur opacity-20 group-hover:opacity-30 transition-opacity duration-300"></div>
              <div className="relative bg-white rounded-2xl shadow-warm border border-accent-100 p-2">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0 pl-4">
                    <Icon name="Sparkles" size={20} className="text-primary" />
                  </div>
                  <Input
                    ref={searchInputRef}
                    type="text"
                    placeholder={t('homepage:hero.searchPlaceholder')}
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    onKeyPress={(e) => handleKeyPress(e, handleSearch)}
                    onFocus={handleInputFocus}
                    className="flex-1 border-0 bg-transparent text-lg placeholder:text-accent-400 focus:ring-0 focus:outline-none"
                  />
                  {isSearching && (
                    <div className="flex-shrink-0 pr-2">
                      <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}
                  <Button
                    variant="primary"
                    onClick={() => handleSearch()}
                    className="rounded-xl px-6 py-3 shadow-primary hover:shadow-lg transition-all duration-300"
                    iconName="Search"
                    iconPosition="left"
                  >
                    {t('common:buttons.findGifts')}
                  </Button>
                </div>
              </div>
            </div>

            {/* Search Dropdown */}
            <div ref={searchDropdownRef}>
              <SearchDropdown
                searchQuery={searchQuery}
                searchResults={searchResults}
                searchSuggestions={searchSuggestions}
                searchHistory={searchHistory}
                isSearching={isSearching}
                searchError={searchError}
                showDropdown={showDropdown}
                onSuggestionClick={handleSuggestionClick}
                onProductClick={handleProductClick}
                onHistoryClick={handleHistoryClick}
                onClearHistory={clearSearchHistory}
                onClose={handleDropdownClose}
              />
            </div>

            {/* Static Search Suggestions */}
            {!showDropdown && (
              <div className="mt-4 flex flex-wrap justify-center gap-2">
                {staticSearchSuggestions.slice(0, 4).map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => handleSuggestionClick(suggestion)}
                    className="text-sm text-accent-500 hover:text-primary bg-white/60 hover:bg-white/80 rounded-full px-3 py-1 transition-all duration-200 border border-accent-100 hover:border-primary-200"
                  >
                    "{suggestion}"
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Quick Action Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <Button
              variant="secondary"
              size="lg"
              iconName="Heart"
              iconPosition="left"
              onClick={() => navigate('/wishlists')}
              className="shadow-soft hover:shadow-md transition-all duration-300"
            >
              {t('common:buttons.viewWishlists')}
            </Button>
          </div>

          {/* Social Proof */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-8 text-center">
            <div className="flex items-center space-x-2">
              <div className="flex -space-x-2">
                <img
                  src="https://images.unsplash.com/photo-1494790108755-2616b612b786?w=40&h=40&fit=crop&crop=face"
                  alt="Happy user"
                  className="w-8 h-8 rounded-full border-2 border-white shadow-sm"
                />
                <img
                  src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=40&h=40&fit=crop&crop=face"
                  alt="Happy user"
                  className="w-8 h-8 rounded-full border-2 border-white shadow-sm"
                />
                <img
                  src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=40&h=40&fit=crop&crop=face"
                  alt="Happy user"
                  className="w-8 h-8 rounded-full border-2 border-white shadow-sm"
                />
              </div>
              <span className="text-sm font-medium text-accent-700">
                {t('homepage:hero.socialProof.meaningfulMoments')}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="flex text-warning-400">
                {[...Array(5)].map((_, i) => (
                  <Icon key={i} name="Star" size={16} className="fill-current" />
                ))}
              </div>
              <span className="text-sm font-medium text-accent-700">
                {t('homepage:hero.socialProof.reviews')}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;